import NumbersIcon from '@mui/icons-material/Numbers';
import {ClickAwayListener, css, InputAdornment, Popper, useTheme} from '@mui/material';
import {DlMuiChipButton, DlMuiSlider, DlMuiTextField, GetStylesFunction} from '@wallaby-ui/core';
import {debounce, isEmpty, isEqual, isNil, isNumber, isFinite} from 'lodash';
import * as React from 'react';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {getNumberRange} from 'src/js/containers/number_range/number_range_selectors';
import {DynamicFilter, Filter, FILTER_TYPE_NUMBER_RANGE} from 'src/js/lib/filtering';
import {FilterNode as FilterNodeModel} from 'src/js/model/filters_config';
import {useAppSelector} from 'src/js/store_hooks';

import {FILTER_TYPE_NUMBER_RANGE_MAX, FILTER_TYPE_NUMBER_RANGE_MIN, FILTER_TYPE_NUMBER_RANGE_STEP} from '../filtering_constants';

interface OwnProps {
  node: FilterNodeModel;
  changeFilter: (filter: Filter) => void;
  removeFilter: (filter: Filter) => void;
  selectedFilter?: Filter;
  min?: number;
  max?: number;
}

type Props = OwnProps;

export const FilterNodeTypeNumberRange: React.FC<Props> = (props) => {
  const {node, selectedFilter, changeFilter, removeFilter, min, max} = props;
  const numberRange = useAppSelector((state) => getNumberRange(state, node.getNumberRangeFilterName()));
  const theme = useTheme();
  const styles = getStyles({theme});
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const isPopperOpened = Boolean(anchorEl);

  const mouseEventClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      setAnchorEl(isPopperOpened ? null : event.currentTarget);
    },
    [isPopperOpened]
  );

  const getFilter = useCallback(
    (value) => {
      const NumberRangeFilter = DynamicFilter.getFilterClass(FILTER_TYPE_NUMBER_RANGE);
      return new NumberRangeFilter({
        node,
        type: FILTER_TYPE_NUMBER_RANGE,
        property: node.getName(),
        value,
      });
    },
    [node]
  );

  const changeFilterFunction = useCallback(
    (value) => {
      const filter = getFilter(value);
      changeFilter(filter);
    },
    [changeFilter, getFilter]
  );

  const debouncedChangeFilter = useMemo(() => debounce(changeFilterFunction, 300), [changeFilterFunction]);

  const getMinFromProps = useCallback(() => {
    let minFromProps = FILTER_TYPE_NUMBER_RANGE_MIN;
    if (!isNil(node.getMin())) {
      minFromProps = node.getMin();
    } else if (!isNil(min)) {
      minFromProps = min;
    }
    return minFromProps;
  }, [min, node]);

  const getMaxFromProps = useCallback(() => {
    let minFromProps = FILTER_TYPE_NUMBER_RANGE_MAX;
    if (!isNil(node.getMax())) {
      minFromProps = node.getMax();
    } else if (!isNil(max)) {
      minFromProps = max;
    }
    return minFromProps;
  }, [max, node]);

  const handleOnChange = (event, value) => {
    setSliderValue(value);
    // set local value according to slider value
    setTextValue(sliderValue);
  };

  const handleOnChangeCommitted = (event, value) => {
    handleOnChange(event, value);
    debouncedChangeFilter(value);
  };

  const getMin = useCallback(() => (isNil(numberRange?.min) ? getMinFromProps() : numberRange.min), [getMinFromProps, numberRange?.min]);
  const getMax = useCallback(() => (isNil(numberRange?.max) ? getMaxFromProps() : numberRange.max), [getMaxFromProps, numberRange?.max]);

  const getValueFromProps = useCallback(
    () => (selectedFilter ? selectedFilter.getValue() : [getMin(), getMax()]),
    [getMax, getMin, selectedFilter]
  );

  const value = useMemo(() => getValueFromProps(), [getValueFromProps]);

  const [textValue, setTextValue] = useState<[number | '', number | '']>(value);
  const [sliderValue, setSliderValue] = useState<[number, number]>(value);

  const getStep = () => {
    return node.getStep() && node.getStep() !== null ? node.getStep() : FILTER_TYPE_NUMBER_RANGE_STEP;
  };

  useEffect(() => {
    setTextValue((oldValue) => {
      return isEqual(oldValue, value) ? oldValue : value;
    });
    setSliderValue((oldValue) => {
      return isEqual(oldValue, value) ? oldValue : value;
    });
  }, [value]);

  const handleCancel = () => {
    if (isPopperOpened) {
      setAnchorEl(null);
    }
  };

  const formattedNumberRange = useMemo(() => {
    return sliderValue.join(' - ');
  }, [sliderValue]);

  const isDefaultValue = useMemo(() => isEqual(formattedNumberRange, `${getMin()} - ${getMax()}`), [formattedNumberRange, getMax, getMin]);

  const isDefaultMinValue = isEqual(textValue[0], getMin());
  const isDefaultMaxValue = isEqual(textValue[1], getMax());

  const debouncedMinChange = useMemo(
    () =>
      debounce((eventValue, newLocalValue) => {
        if (newLocalValue.length === 2) {
          // set getMin() value if the input is empty
          if (newLocalValue[0] === '') {
            newLocalValue.splice(0, 1, getMin());
            setTextValue(newLocalValue);
          }
          // set getMin() value if the input is smaller than min value
          if (newLocalValue[0] < getMin()) {
            newLocalValue.splice(0, 1, getMin());
            setTextValue(newLocalValue);
          }
          // set getMax() value if the input is bigger than max value
          if (newLocalValue[0] > getMax()) {
            newLocalValue.splice(0, 1, getMax());
            setTextValue(newLocalValue);
          }
          // set max input to the min input if the min input is bigger than max input value
          if (newLocalValue[0] > newLocalValue[1] && newLocalValue[0] <= getMax()) {
            newLocalValue.splice(1, 1, newLocalValue[0]);
          }
          if (!isEqual(newLocalValue, eventValue)) {
            // change filter value in parent component
            changeFilterFunction(newLocalValue);
          }
          if (newLocalValue[0] !== '') {
            // set slider value according to newLocalValue
            setSliderValue(newLocalValue);
          }
        }
      }, 1000),
    [changeFilterFunction, getMax, getMin]
  );

  const handleMinChange = (event) => {
    let parsedEventValue: number | '' = parseInt(event.target.value, 10);
    const newLocalValue: [number | '', number | ''] = [...textValue];
    if (Number.isNaN(parsedEventValue)) {
      parsedEventValue = '';
    }
    newLocalValue.splice(0, 1, parsedEventValue);
    setTextValue(newLocalValue);
    debouncedMinChange(parsedEventValue, newLocalValue);
  };

  const debouncedMaxChange = useMemo(
    () =>
      debounce((eventValue, newLocalValue) => {
        if (newLocalValue.length === 2) {
          // set max input to getMax() value if input is empty
          if (newLocalValue[1] === '') {
            newLocalValue.splice(1, 1, getMax());
            setTextValue(newLocalValue);
          }
          // set max input to min input value if typed number was smaller than min input
          if (newLocalValue[1] < newLocalValue[0] && newLocalValue[1] < getMax()) {
            newLocalValue.splice(1, 1, newLocalValue[0]);
            setTextValue(newLocalValue);
          }

          // set max input to getMax() value if typed number was higher than max
          if (newLocalValue[1] > getMax()) {
            newLocalValue.splice(1, 1, getMax());
            setTextValue(newLocalValue);
          }

          if (!isEqual(newLocalValue, eventValue)) {
            // change filter value in parent component
            changeFilterFunction(newLocalValue);
          }

          if (newLocalValue[1] !== '') {
            // set slider value according to newLocalValue
            setSliderValue(newLocalValue);
          }
        }
      }, 1000),
    [changeFilterFunction, getMax]
  );

  const handleMaxChange = (event) => {
    let parsedEventValue: number | '' = parseInt(event.target.value, 10);
    const newLocalValue: [number | '', number | ''] = [...textValue];
    if (Number.isNaN(parsedEventValue)) {
      parsedEventValue = '';
    }
    newLocalValue.splice(1, 1, parsedEventValue);
    setTextValue(newLocalValue);
    debouncedMaxChange(parsedEventValue, newLocalValue);
  };

  const isFilterEmpty = isEmpty(selectedFilter);

  return (
    <>
      <div css={styles.textFieldWrapper}>
        <DlMuiTextField
          data-name={'filter-node-type-number-range--input'}
          label={node.getLabel()}
          variant={'outlined'}
          onClick={mouseEventClick}
          InputProps={{
            readOnly: true,
            endAdornment: (
              <InputAdornment position='end'>
                <NumbersIcon />
              </InputAdornment>
            ),
          }}
          value={isFilterEmpty ? [] : formattedNumberRange}
          isDefaultValue={isDefaultValue}
          inputCss={styles.textField}
        />
      </div>
      {isPopperOpened ? (
        <ClickAwayListener onClickAway={handleCancel}>
          <Popper data-name={'dl-mui-popper--popper'} open={isPopperOpened} anchorEl={anchorEl} css={styles.popper}>
            <DlMuiSlider
              data-name={'filter-node-type-number-range--slider'}
              value={sliderValue}
              label={node.getLabel()}
              onChange={handleOnChange}
              onChangeCommitted={handleOnChangeCommitted}
              min={getMin()}
              max={getMax()}
              step={getStep()}
            />
            <div data-name={'filter-node-type-number-range--inputs-wrapper'} css={styles.inputsWrapper}>
              <DlMuiTextField
                data-name={'filter-node-type-number-range--input-min-text-field'}
                onFocus={(event) => event.target.select()}
                inputCss={styles.inputTextField}
                value={textValue[0]}
                isDefaultValue={isDefaultMinValue}
                onChange={handleMinChange}
                label={''}
              />
              <span css={styles.dash}>-</span>
              <DlMuiTextField
                data-name={'filter-node-type-number-range--input-max-text-field'}
                onFocus={(event) => event.target.select()}
                inputCss={styles.inputTextField}
                value={textValue[1]}
                isDefaultValue={isDefaultMaxValue}
                onChange={handleMaxChange}
                label={''}
              />
            </div>
            {isFilterEmpty ? null : (
              <div css={styles.resetFilterButton}>
                <DlMuiChipButton
                  data-name={'filter-node-type-number-range--clear-filters-button'}
                  title={'filter.remove'}
                  onClick={() => selectedFilter && removeFilter(selectedFilter)}
                />
              </div>
            )}
          </Popper>
        </ClickAwayListener>
      ) : null}
    </>
  );
};

FilterNodeTypeNumberRange.displayName = 'FilterNodeTypeNumberRange';

const getStyles: GetStylesFunction = (options) => ({
  textFieldWrapper: css`
    display: flex;
    height: 53px;
    width: 176px;
  `,
  textField: css({
    'width': '100%',
    // marginRight: '10px',
    'height': '38px',
    'top': '8px',
    'cursor': 'pointer',
    '& .MuiInputBase-root': {
      cursor: 'pointer',
    },
    '& .MuiInputBase-input': {
      cursor: 'pointer',
    },
  }),
  popper: css`
    z-index: 1300;
    width: 200px;
    background-color: ${options?.theme?.palette.common.white};
    padding: ${options?.theme?.spacing(3)};
    box-shadow: 0 0 12px 0 #12161914;
    display: flex;
    flex-direction: column;
  `,
  inputsWrapper: css`
    margin-top: 10px;
    display: flex;
    flex-direction: row;
    align-items: center;
    position: relative;
  `,
  dash: css`
    margin: 0 5px;
  `,
  inputTextField: css`
    min-width: 37px;
    justify-content: center;
    border-radius: 4px;
  `,
  resetFilterButton: css`
    margin-top: 5px;
    display: flex;
    justify-content: flex-end;
  `,
});
